<script lang="ts" setup>
import { ref, toRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useClaimStore } from '@/store';

import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { ExclamationCircleIcon } from '@heroicons/vue/24/outline'
import SelectClaimType from './SelectClaimType.vue';
import { ClaimTypeName } from '@/utils/config/constants';

const emit = defineEmits<{(e: 'close'): void}>();
const props = defineProps<{
  users: {
    id: string;
    contact: string | null;
    civility: string;
  }[]
  open: boolean;
}>();

const { claim } = storeToRefs(useClaimStore());

const reason = ref("");
async function extractPassengers() {
  const res = useClaimStore().extractPassengers(claim.value.id, {
    usersIds: props.users.map(u => u.id),
    claimTypeName: newClaimTypeName.value,
    reason: reason.value,
  });

  close();
}

const newClaimTypeName = ref<ClaimTypeName>(useClaimStore().claim.claimType.name);
const isOpen = toRef(props, 'open');
const step = ref(1);

function close() {
  emit('close');
  step.value = 1;
}
</script>
<template>
<!-- @TODO Generic modal component -->
  <TransitionRoot as="template" :show="isOpen">
    <Dialog as="div" class="fixed inset-0 z-10 overflow-y-auto" @close="close">
      <div class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <DialogOverlay class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden -mt-20 sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
          <div class="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div class="px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
              <div class="sm:flex sm:items-start">
                <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationCircleIcon class="w-6 h-6 text-red-600" aria-hidden="true" />
                </div>
                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900">Extraire des passagers</DialogTitle>
                  <div v-if="step === 1" class="my-2">
                    <p class="text-sm text-gray-500">
                      Êtes-vous sûr de vouloir extraire <span v-if="users.length > 1">les passagers selectionnés</span><span v-else>le passager selectionné</span> ? Cette action est irréversible.
                    </p>
                    <div class="mt-6">
                      <ul v-for="user in users" :key="user.id">
                        <li class="my-3 list-none">
                          <p class="text-sm">{{ user.civility }} <span class="ml-2 text-xs text-gray-500">{{ user.contact }}</span></p>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div v-else-if="step === 2" class="my-2">
                    <p class="text-sm text-gray-500">Veuillez selectionner le type de la nouvelle claim</p>
                    <SelectClaimType
                      :users-count="users.length"
                      @selection-update="(e: any) => newClaimTypeName = e"
                      class="py-4"
                    />
                  </div>
                  <div v-else-if="step === 3" class="my-2">
                    <p class="text-sm text-gray-500">Pour des raisons d'historique, veuillez indiquer la raison qui vous pousse à effectuer cette action</p>
                    <textarea
                      v-model="reason"
                      class="w-full p-2 my-4 border-2 border-gray-200 rounded-md hover:border-gray-300"
                    >
                    </textarea>
                  </div>
                </div>
              </div>
            </div>
            <div class="px-4 py-3 bg-gray-50 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                v-if="step === 1 || step === 2"
                @click="step++"
                type="button"
                class="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                {{ step === 1 ? 'Oui, suivant' : 'Valider' }}
              </button>
              <button
                v-else-if="step === 3"
                @click="extractPassengers"
                type="button"
                class="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Extraire
              </button>
              <button
                @click="() => { step === 1 ? close() : step-- }"
                type="button"
                class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                ref="cancelButtonRef"
              >
                {{ step === 1 ? 'Annuler' : 'Retour' }}
              </button>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>